import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import authService from '../../../api-authorization/AuthorizeService';
import { inchesToFeet, feetToInches } from '../../../../utils';

const UpdateTractorDialog = ({
  tractor,
  device,
  open,
  onClose,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [targetApplicationRates, setTargetApplicationRates] = useState([]);

  const { handleSubmit, register, watch, setValue } = useForm({
    defaultValues: {
      ...tractor,
      targetPressurePsi: tractor?.targetPressurePsi ?? '',
      rowMultiplier: tractor?.rowMultiplier ?? 1,
      // Device fields override tractor fields if present
      serialNumber: device?.serialNumber || '',
      flowPulsesPerGal1: device?.flowPulsesPerGal1 ?? '',
      flowPulsesPerGal2: device?.flowPulsesPerGal2 ?? '',
      flowPulsesPerGal3: device?.flowPulsesPerGal3 ?? '',
      xOffsetFt: feetToInches(device?.xOffsetFt ?? ''),
      yOffsetFt: feetToInches(device?.yOffsetFt ?? ''),
      extXOffsetFt: feetToInches(device?.extXOffsetFt ?? ''),
      extYOffsetFt: feetToInches(device?.extYOffsetFt ?? ''),
      installLocation: device?.installLocation ?? '',
    }
  });
  const defaultTargetApplicationRateGalAcre = watch('defaultTargetApplicationRateGalAcre', tractor?.defaultTargetApplicationRateGalAcre || '');

  // Set initial values when tractor changes
  useEffect(() => {
    if (tractor) {
      setValue('name', tractor.name);
      setValue('defaultTargetApplicationRateGalAcre', tractor.defaultTargetApplicationRateGalAcre);
      setValue('tankSizeGal', tractor.tankSizeGal);
      setValue('targetPressurePsi', tractor.targetPressurePsi ?? '');
      setValue('rowMultiplier', tractor.rowMultiplier ?? 1);
    }
    if (device) {
      setValue('serialNumber', device.serialNumber || '');
      setValue('flowPulsesPerGal1', device.flowPulsesPerGal1 ?? '');
      setValue('flowPulsesPerGal2', device.flowPulsesPerGal2 ?? '');
      setValue('flowPulsesPerGal3', device.flowPulsesPerGal3 ?? '');
      setValue('xOffsetFt', feetToInches(device?.xOffsetFt ?? ''));
      setValue('yOffsetFt', feetToInches(device?.yOffsetFt ?? ''));
      setValue('extXOffsetFt', feetToInches(device?.extXOffsetFt ?? ''));
      setValue('extYOffsetFt', feetToInches(device?.extYOffsetFt ?? ''));
      setValue('installLocation', device.installLocation ?? '');
    }
  }, [tractor, device, setValue]);

  // Fetch targetApplicationRates when dialog opens or tractor.clientId changes
  useEffect(() => {
    const fetchRates = async () => {
      if (!tractor?.clientId) return;
      const token = await authService.getAccessToken();
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/api/clients`,
          {
            headers: !token
              ? {}
              : {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
          }
        );
        if (response.ok) {
          const resp = await response.json();
          const rates = resp.targetApplicationRates;
          const sortedRates = rates.sort((a, b) => a - b);
          setTargetApplicationRates(sortedRates);
        } else {
          setTargetApplicationRates([]);
        }
      } catch {
        setTargetApplicationRates([]);
      }
    };
    if (open) {
      fetchRates();
    }
  }, [tractor, open, setValue]);

  const updateTractor = async (data) => {
    if (
      !data.name ||
      !data.defaultTargetApplicationRateGalAcre ||
      !data.tankSizeGal ||
      !data.targetPressurePsi
    ) {
      return toast.error('Please complete all required fields.');
    }
    if (Number(data.rowMultiplier) < 1) {
      return toast.error('Row Multiplier must be 1 or greater.');
    }

    const token = await authService.getAccessToken();
    setLoading(true);
    try {
      const dataInFeet = {
        ...data,
        xOffsetFt: inchesToFeet(data.xOffsetFt),
        yOffsetFt: inchesToFeet(data.yOffsetFt),
        extXOffsetFt: inchesToFeet(data.extXOffsetFt),
        extYOffsetFt: inchesToFeet(data.extYOffsetFt)
      };

      // Update Tractor
      const tractorResponse = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/admin/tractors/update`,
        {
          method: 'PUT',
          headers: !token
            ? {}
            : {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
          body: JSON.stringify(dataInFeet)
        }
      );

      // Update Device
      const devicePayload = {
        id: device?.id,
        serialNumber: dataInFeet.serialNumber,
        flowPulsesPerGal1: dataInFeet.flowPulsesPerGal1,
        flowPulsesPerGal2: dataInFeet.flowPulsesPerGal2,
        flowPulsesPerGal3: dataInFeet.flowPulsesPerGal3,
        xOffsetFt: dataInFeet.xOffsetFt,
        yOffsetFt: dataInFeet.yOffsetFt,
        extXOffsetFt: dataInFeet.extXOffsetFt,
        extYOffsetFt: dataInFeet.extYOffsetFt,
        installLocation: dataInFeet.installLocation
      };
      const deviceResponse = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/admin/devices/update`,
        {
          method: 'PUT',
          headers: !token
            ? {}
            : {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
          body: JSON.stringify(devicePayload)
        }
      );

      if (tractorResponse.ok && deviceResponse.ok) {
        toast.success('Sprayer and device updated successfully!');
        onSuccess();
      } else {
        let errorMsg = '';
        if (!tractorResponse.ok) {
          errorMsg += `Tractor: ${tractorResponse.status} ${tractorResponse.statusText}. `;
        }
        if (!deviceResponse.ok) {
          errorMsg += `Device: ${deviceResponse.status} ${deviceResponse.statusText}.`;
        }
        toast.error(errorMsg.trim());
      }
    } catch (err) {
      toast.error('Error: ' + err);
      console.log('err', err);
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <form onSubmit={handleSubmit(updateTractor)}>
        <DialogTitle>
          <EditIcon /> Edit Sprayer
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Stack direction="row" spacing={2}>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Sprayer Name</FormLabel>
                <TextField autoFocus required {...register('name')} />
              </FormControl>
            </Stack>
            <Stack direction="row" spacing={2}>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Def Target App Rate (gal/acre)</FormLabel>
                <Select
                  required
                  value={defaultTargetApplicationRateGalAcre}
                  {...register('defaultTargetApplicationRateGalAcre')}
                  onChange={e => setValue('defaultTargetApplicationRateGalAcre', e.target.value)}
                  displayEmpty
                >
                  {targetApplicationRates.length === 0 && (
                    <MenuItem value="">
                      <em>No rates available</em>
                    </MenuItem>
                  )}
                  {targetApplicationRates.map((rate) => (
                    <MenuItem key={rate} value={rate}>
                      {rate}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Target Pressure (psi)</FormLabel>
                <TextField
                  required
                  type="number"
                  {...register('targetPressurePsi')}
                  InputProps={{ inputProps: { step: 'any' } }}
                />
              </FormControl>
            </Stack>
            <Stack direction="row" spacing={2}>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Row Multiplier</FormLabel>
                <TextField
                  required
                  type="number"
                  {...register('rowMultiplier')}
                  InputProps={{ inputProps: { step: 1, min: 1 } }}
                />
              </FormControl>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Tank Size (gal)</FormLabel>
                <TextField
                  required
                  type="number"
                  {...register('tankSizeGal')}
                  InputProps={{ inputProps: { step: 'any' } }}
                />
              </FormControl>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" sx={{ mb: 1 }}>
              Agtellio-S Configuration
            </Typography>

            <FormControl>
              <FormLabel>Install Location</FormLabel>
              <TextField
                required
                {...register('installLocation')}
              />
            </FormControl>

            <Stack direction="row" spacing={2}>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Flow 1 (pul/gal)</FormLabel>
                <TextField
                  required
                  type="number"
                  {...register('flowPulsesPerGal1')}
                  InputProps={{ inputProps: { step: 'any' } }}
                />
              </FormControl>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Flow 2 (pul/gal)</FormLabel>
                <TextField
                  required
                  type="number"
                  {...register('flowPulsesPerGal2')}
                  InputProps={{ inputProps: { step: 'any' } }}
                />
              </FormControl>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Flow 3 (pul/gal)</FormLabel>
                <TextField
                  required
                  type="number"
                  {...register('flowPulsesPerGal3')}
                  InputProps={{ inputProps: { step: 'any' } }}
                />
              </FormControl>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" sx={{ mb: 1 }}>
              GNSS Configuration
            </Typography>

            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              GNSS Receiver Offsets
            </Typography>
            <Stack direction="row" spacing={2}>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>+Right / -Left (in)</FormLabel>
                <TextField
                  required
                  type="number"
                  {...register('xOffsetFt')}
                  InputProps={{ inputProps: { step: 'any' } }}
                />
              </FormControl>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>+Fwd / -Back (in)</FormLabel>
                <TextField
                  required
                  type="number"
                  {...register('yOffsetFt')}
                  InputProps={{ inputProps: { step: 'any' } }}
                />
              </FormControl>
            </Stack>

            <Typography variant="subtitle2" sx={{mb: 0.5 }}>
              External GNSS Receiver Offsets
            </Typography>
            <Stack direction="row" spacing={2}>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>+Right / -Left (in)</FormLabel>
                <TextField
                  required
                  type="number"
                  {...register('extXOffsetFt')}
                  InputProps={{ inputProps: { step: 'any' } }}
                />
              </FormControl>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>+Fwd / -Back (in)</FormLabel>
                <TextField
                  required
                  type="number"
                  {...register('extYOffsetFt')}
                  InputProps={{ inputProps: { step: 'any' } }}
                />
              </FormControl>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" variant="outlined" disabled={loading}>
            Save Changes
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UpdateTractorDialog;